import React, { useEffect, useState, useContext, memo, useMemo, useRef } from "react";
import { Polygon, InfoWindow } from "@react-google-maps/api";
import tinygradient from "tinygradient";
import axios from "axios";
import {
  getOprtMarketShareData,
  getTopMSTooltipData,
} from "../../../../services/api/marketShare.api";
import { getPolygonByAdminLevel } from "../../../../services/api/mapLocation.api";
import {
  getPolygonFromApiResp,
  getPolygonsForMarketShare,
  getPolygonsWithMarketShare,
  getUniqueGeohashes,
  paginatedApiInvoker,
} from "../../../../utils/webMapApiHelper";
import { WebMapContext } from "../../../../pages/sections/web-map/WebMapGoogleMap";
import {
  convertArrayToStringWithDelimeter,
  convertGeohashToPolygon,
} from "../../../../utils/helper";
import { pageSizeByADM } from "../../../../constants/WebMapApi";
import { getMiddle, shouldAddToQuery } from "../../../../helpers/webMap";
import { defaultGradientArr, OP_MS_RADIO_DATA, OP_MS_TRANSMISSION_DATA } from "../../../../constants/Data";
import { mapValueToRange } from "../../../../helpers/fuctionHelpers";
import { MapTools, Modules, SEARCH_AREA_STATES, TooltipType, bestOpTypeKeyMapper, geohashMiddle } from "../../../../constants/WebMap";
import OperatorTooltip from "../../OperatorTooltip";
import { getCustomOperatorGeohashes, getOperatorTooltipData } from "../../../../services/api/mapGeohashes.api";
import TopMSTooltip from "../../TopMSTooltip";
import { BackDropWithText } from "../../Loader";

const OperatorMSMapElements = ({
  borderLvl,
  connectionType,
  aggType,
  operator,
  msRangeFrom = 0,
  msRangeTo = 100,
  msType,
  msLevel = [],
  action = [],
  quad = [],
  selectedMS = [],
  msRanks = [],
  apiTrigger,
  borderLvls,
  mapPriority = "divTable",
  selectedGeohashes = [],
  eyeIcon,
  radioValue,
  permittedOperators,
  minMaxMSOp
}) => {
  let cancelToken;
  let timeOutForApiCall = 2000;
  const polygonApiTO = useRef();
  const geohashApiTO = useRef();

  const [msPolygons, setMsPolygons] = useState([]);
  const [msPolygonBorder, setMsPolygonBorder] = useState([]);
  const [msGeohashes, setMsGeohashes] = useState([]);
  const [geoTooltip, setGeoTooltip] = useState();
  const [opTooltip, setOpTooltip] = useState();
  const [isLoadingMarketShare, setIsLoadingMarketShare] = useState(false);
  const [anomOp, setAnomOp] = useState({});

  const { appliedFilters, msConfig = [], mapRef, setMarketShareLegendData, searchAreaBtn,
    storeLastDataLoadedBounds, getBbox, setApisOngoing, setSearchAreaBtn, geohashNE, geohashSW,
    geohashZoom, mapZoomLevel, boundsChanged, globalConfig, mapTool, setDownloadData} = useContext(WebMapContext);

  const { countryCode, year, quarter, level, levelVal } = appliedFilters;
  const skipLevelFilters = [
    OP_MS_RADIO_DATA[5].value,
    OP_MS_RADIO_DATA[6].value,
    OP_MS_RADIO_DATA[7].value,
    OP_MS_RADIO_DATA[8].value,
  ];
  const geoTooltipTypeMap = {
    l_rsrp: 'RSRP',
    l_down: 'Download',
    l_up: 'Upload',
  }

  useEffect(() => {
    setMsPolygons([]);
    setGeoTooltip();
    setAnomOp({});
    setOpTooltip(null);
  }, [countryCode, year, quarter]);

  useEffect(() => {
    setOpTooltip(null);
    setGeoTooltip(null);
    setAnomOp({});
    setMsPolygonBorder([]);
    setMsPolygons([]);
  }, [borderLvl, connectionType, operator, msType, radioValue, eyeIcon, apiTrigger, levelVal, level, selectedMS, selectedGeohashes])

  useEffect(() => {
    if (geohashZoom && mapZoomLevel < geohashZoom && !selectedGeohashes.length) setMsGeohashes([]);
  }, [mapZoomLevel, geohashZoom]);

  useEffect(() => { // all market share on map
    if (map_cond) drawOnMap();
  }, [JSON.stringify(borderLvls), apiTrigger, JSON.stringify(selectedMS), JSON.stringify(selectedGeohashes)]);

  useEffect(() => { // selected ms on map
    if (mapPriority !== "geoTable" && selectedMS.length > 0) drawSelectedOnMap();
  }, [selectedMS, msLevel]);

  useEffect(() => { // selected geohash on map
    setGeoTooltip();
    setAnomOp({});
    if (mapPriority === "geoTable" && selectedGeohashes.length > 0) {
      setMsGeohashes(selectedGeohashes);
      const polygons = selectedGeohashes.map((x) => {
        return convertGeohashToPolygon(x.geohash);
      });
      fitPolygonBounds(polygons);
    }
  }, [selectedGeohashes]);

  useEffect(() => { // any geohash on map
    if (geohash_cond) {
      fetchGeohashesVP();
      storeLastDataLoadedBounds(); // save data loaded bounds
    }
  }, [
    geohashSW,
    level,
    levelVal,
    mapPriority,
    apiTrigger,
    geohashZoom,
    boundsChanged
  ]);

  useEffect(() => {  // any geohash on map when click on search area btn
    if (geohash_cond && searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      fetchGeohashesVP();
      storeLastDataLoadedBounds(); // save data loaded bounds
    }
  }, [searchAreaBtn]);

  useEffect(() => {
      if (eyeIcon && msConfig && radioValue) generateMarketShareLegend(radioValue);
      else setMarketShareLegendData([]);
    }, [eyeIcon, msConfig, radioValue, minMaxMSOp]);


  const map_cond = useMemo(() => {
    const quadAvailable = quad.filter((x) => !x.disabled);
    return connectionType && quadAvailable.length > 0 && !selectedMS.length && !selectedGeohashes.length && mapPriority !== "geoTable";
  }, [connectionType, quad, selectedMS, selectedGeohashes, mapPriority]);

  const geohash_cond = useMemo(() => {
    return geohashZoom && mapZoomLevel >= geohashZoom &&
      mapPriority === "geoTable" && !selectedGeohashes.length;
  }, [geohashZoom, mapZoomLevel, mapPriority, selectedGeohashes]);

  const msConfigColors = useMemo(() => {
    if (msConfig) {
      const section = msConfig[0]?.content.find((x) => x.type === msType);
      return section ? section.values : msConfig[0]?.content[0].values;
    }
    return null;
  }, [msConfig, msType]);

  const gradient = useMemo(() => {
    if ((OP_MS_RADIO_DATA[7].value === msType) &&
      msConfigColors &&
      "colorGradient" in msConfigColors[0]
    ) {
      return tinygradient(msConfigColors[0].colorGradient);
    }
    return null;
  }, [msConfigColors, msType]);

  const msPossibleRange = useMemo(() => {
    let range = [];
    if (msType === OP_MS_RADIO_DATA[7].value) range = [0, 100];
    return range;
  }, [msType, msRanks]);

  const drawOnMap = async () => {
    if (typeof cancelToken != typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    setMsPolygons([])
    //For download feature
    setDownloadData((prevData) => ({
      ...prevData,
      [Modules.MARKET_SHARE_OPERATOR]: [],
    }));
    setMsPolygonBorder([])
    //Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    clearTimeout(polygonApiTO.current);

    setIsLoadingMarketShare(true);
    polygonApiTO.current = setTimeout(async () => {
      try {
        // setMsPolygons([]);
        const addRanks = shouldAddToQuery(msRanks);
        const addActions = shouldAddToQuery(action);
        const quadFilters = quad
          .filter((x) => x.isChecked)
          .map((x) => x.filterName);
        const msFilters = msLevel
          .filter((x) => x.isChecked)
          .map((x) => x.filterName);
        const checkedQuads = convertArrayToStringWithDelimeter(quadFilters, "_");
        const addMsFilters = (!skipLevelFilters.includes(msType) && !OP_MS_TRANSMISSION_DATA.some(item => item.value === msType))
          ? convertArrayToStringWithDelimeter(msFilters, "_") // send "val1_val2" or "_" to BE. In BE it reads as ["val1",...] or []
          : null; // else send nothing, so BE will omit this as a filter
        const { data: msResp } = await getOprtMarketShareData(
          {
            country: countryCode,
            quarter: `${quarter} ${year}`,
            borderLvl,
            connectionType,
            ...(aggType && { aggType }),
            ...(level && { level }),
            ...(levelVal && { levelVal }),
            operator,
            msRangeFrom,
            msRangeTo,
            type: msType,
            ...(addRanks && {
              ranks: convertArrayToStringWithDelimeter(addRanks, "_"),
            }),
            ...(addActions && {
              action: convertArrayToStringWithDelimeter(addActions, "_"),
            }),
            quad: checkedQuads || "_",
            ...(addMsFilters !== null && { filter: addMsFilters || "_" }),
          },
          100000,
          1,
          cancelToken
        );
        const dataLvl = borderLvl === "ADM1" ? "ADM2" : borderLvl; // due to overlay issue in ADM1, polygons will fetch for ADM2 & 3 only
        const perPage = pageSizeByADM[`${countryCode}_${dataLvl}`] || 70;
        const admResp = await paginatedApiInvoker(
          (pageSize, page) =>
            getPolygonByAdminLevel(
              {
                borderLvl: dataLvl,
                ...appliedFilters,
              },
              pageSize,
              page,
              true,
              cancelToken
            ),
          perPage,
          () => setApisOngoing(count => count + 1),
          null,
          () => setApisOngoing(count => count - 1),
          true
        );

        const marketShares = getPolygonsWithMarketShare(admResp, msResp, borderLvl);
        setMsPolygons(prevState => [...prevState, ...marketShares]);
        //For download feature
        setDownloadData((prevData) => ({
          ...prevData,
          [Modules.MARKET_SHARE_OPERATOR]: [
            ...(prevData[Modules.MARKET_SHARE_OPERATOR] || []),
            ...marketShares,
          ],
        }));
        if (borderLvl === "ADM1") drawADM1Border();
      } catch (e) {
        console.log(e)
      } finally {
        setIsLoadingMarketShare(false);
      }
    }, timeOutForApiCall);
  };

  const drawSelectedOnMap = async () => {
    setMsPolygons([]);
    //For download feature
    setDownloadData((prevData) => ({
      ...prevData,
      [Modules.MARKET_SHARE_OPERATOR]: [],
    }));
    const msFilters = msLevel
      .filter((x) => x.isChecked)
      .map((x) => x.filterName);
    const filterByLvls = selectedMS.filter((x) =>
      msFilters.includes(x[msType]) || skipLevelFilters.includes(msType)
    );
    const dataLvl = borderLvl === "ADM1" ? "ADM2" : borderLvl; // due to overlay issue in ADM1, polygons will fetch for ADM2 & 3 only
    const perPage = pageSizeByADM[`${countryCode}_${dataLvl}`] || 70;
    const admResp = await paginatedApiInvoker(
      (pageSize, page) =>
        getPolygonByAdminLevel(
          {
            borderLvl: dataLvl,
            ...appliedFilters,
          },
          pageSize,
          page,
          true
        ),
      perPage,
      () => setApisOngoing(count => count + 1),
      null,
      () => setApisOngoing(count => count - 1),
      true
    );
    const marketShares = getPolygonsForMarketShare(
      filterByLvls,
      admResp,
      borderLvl
    );
    setMsPolygons(prevState => [...prevState, ...marketShares]);
    //For download feature
    setDownloadData((prevData) => ({
      ...prevData,
      [Modules.MARKET_SHARE_OPERATOR]: [
        ...(prevData[Modules.MARKET_SHARE_OPERATOR] || []),
        ...marketShares,
      ],
    }));
    if (marketShares.length) {
      const polys = marketShares.map(x => x.poly);
      fitPolygonBounds(polys);
    }
    if (borderLvl === "ADM1") drawADM1Border(filterByLvls);
  };

  const drawADM1Border = async (selectedAdms = []) => {
    const selectedAdm1s = selectedAdms.map(x => x.adm1);
    const perPageBorderApi = pageSizeByADM[`${countryCode}_ADM1`] || 70;
    await paginatedApiInvoker(
      (pageSize, page) =>
        getPolygonByAdminLevel(
          {
            borderLvl: "ADM1",
            ...appliedFilters,
          },
          pageSize,
          page,
          true,
          cancelToken
        ),
      perPageBorderApi,
      () => setMsPolygonBorder([]),
      (data) => {
        if (data) {
          let matchedAdms = data;
          if (selectedAdm1s.length) matchedAdms = matchedAdms.filter(x => selectedAdm1s.includes(x.adminLevel1));
          const polygons = getPolygonFromApiResp(matchedAdms);
          setMsPolygonBorder(prevState => [...prevState, ...polygons]);
        }
      },
      null,
      false
    );
  }

  const fetchGeohashesVP = async () => {
    setMsGeohashes([]);
    setGeoTooltip();
    setAnomOp({});
    if (typeof cancelToken != typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    //Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    clearTimeout(geohashApiTO.current);

    geohashApiTO.current = setTimeout(async () => {
      try {
        const addActions = shouldAddToQuery(action);
        const quadFilters = quad
          .filter((x) => x.isChecked)
          .map((x) => x.filterName);
        const checkedQuads = convertArrayToStringWithDelimeter(quadFilters, "_");
        const { neBound, swBound } = getBbox;
        await paginatedApiInvoker(
          (pageSize, page) => getCustomOperatorGeohashes(
            {
              ...appliedFilters,
              operator,
              ct: connectionType,
              dt: "Macro",
              ...(aggType? {at:aggType} : null),
              northEast: convertArrayToStringWithDelimeter(geohashNE.length ? geohashNE : neBound, "_"),
              southWest: convertArrayToStringWithDelimeter(geohashSW.length ? geohashSW : swBound, "_"),
              level: level !== 'geoHashLevel' ? level : null,
              levelVal: level !== 'geoHashLevel' ? levelVal : null,
              ...(addActions && {
                action: convertArrayToStringWithDelimeter(addActions, "_"),
              }),
              quad: checkedQuads || "_",
            }, cancelToken, pageSize, page
          ),
          30000, // perPage
          () => setApisOngoing(count => count + 1),
          (data) => {
            if (data) setMsGeohashes(prevState => [...getUniqueGeohashes([...prevState, ...data])]);
          },
          () => {
            if (!level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
            setApisOngoing(count => count - 1);
          }
        );
      } catch (err) {
        console.log(err);
      }
    }, timeOutForApiCall);
  };

  const fitPolygonBounds = (polygons) => {
    if (mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      polygons.forEach((polygon) => {
        polygon.forEach((latLng) => bounds.extend(latLng));
      });
      mapRef.current.fitBounds(bounds);
    }
  };

  const getBuildColor = (val) => {
      if(msType){

      const msLegend = msConfig.find(section => section.content.some(contentItem => contentItem.type === msType));
      if (msLegend) {
        const gradientArr = msLegend.content.find(contentItem => contentItem.type === msType)?.values[0]?.colorGradient.map(obj => {
          const { _id, ...rest } = obj;
          return rest;
        });
          const min = parseInt(minMaxMSOp[msType]?.min) || 0;
          const max = parseInt(minMaxMSOp[msType]?.max) || 1;
          const norm = mapValueToRange(val, min, max);
          if (norm < 0 || norm > 1) return null;
          return tinygradient(gradientArr)?.rgbAt(Number(norm));
        }
      }
  };

  const getLvlColor = (msData) => {
    if (!msConfigColors || !msData.ms) return null;
    const lvlVal = msData.ms[msType];
    if(OP_MS_RADIO_DATA[7].value === msType){
      if (msPossibleRange.length && gradient) {
          if (lvlVal >= msPossibleRange[0] && lvlVal <= msPossibleRange[1]) {
            return gradient.rgbAt(Number(mapValueToRange(lvlVal, msPossibleRange[0], msPossibleRange[1])));
          }
        return "rgba(0,0,0,0)";
        }
    }else if(OP_MS_TRANSMISSION_DATA.some(item => item.value === msType)){
      return getBuildColor(Number(lvlVal));
    }else{
      const colorObj = msConfigColors.find((x) => x.colorText === lvlVal);
      return colorObj?.color;
    }
  };

  const getGeohashColor = (data = {}) => {
    let type = msType;
    const msToSupplyMapper = {
      "l_rsrp": "rsrp_level",
      "l_down": "download_level",
      "l_up": "upload_level",
    }
    if (mapPriority === "geoTable" && !selectedGeohashes.length && msType in msToSupplyMapper) type = msToSupplyMapper[msType];
    if (!type in data) return null;
    const lvlVal = data[type];
    const checkedLvls = msLevel.filter(x => x.isChecked).map(x => x.filterName);
    if ((msLevel.length && !checkedLvls.length) || (checkedLvls.length && !checkedLvls.includes(lvlVal))) return null; // deselected itms
    const colorObj = msConfigColors.find((x) => x.colorText === lvlVal);
    return colorObj?.color;
  };

  const processResponseData = (responseData, permittedOperators, anomData) => {
    const processedData = {};  // Use an object to store the processed data by key

    for (const key in responseData) {
        const operator = responseData[key].operator;
        const anonymized = anomData.find(anon => anon.operator === operator);
        const permittedOperator = permittedOperators.find(op => op.operator === operator);

        // If the operator is permitted
        if (permittedOperator) {
            if (!permittedOperator.isAnonymized) {
                // If the operator is permitted and not anonymized, use the real name
                processedData[key] = {
                    ...responseData[key],
                    name: permittedOperator.name,
                    isAnonymized: false
                };
            } else {
                // If the operator is permitted but anonymized, use the anonymized name
                processedData[key] = {
                    ...responseData[key],
                    name: anonymized ? anonymized.anonymizedName : "",
                    isAnonymized: true
                };
            }
        } else {
            // If the operator is not permitted, anonymize it
            processedData[key] = {
                ...responseData[key],
                name: anonymized ? anonymized.anonymizedName : "",
                isAnonymized: true
            };
        }
    }

    return processedData;
};

  const onClickGeohash = async (operatorData) => {
    console.log()
    try {

      const mappedOperatorData = {
        ...operatorData,
        download_level: operatorData.l_down !== undefined ? operatorData.l_down : operatorData.download_level,
        rsrp_level: operatorData.l_rsrp !== undefined ? operatorData.l_rsrp : operatorData.rsrp_level,
        upload_level: operatorData.l_up !== undefined ? operatorData.l_up : operatorData.upload_level,
      };

      const selectedOp = permittedOperators.find((op) => op.operator === operator);
      const anom = {
        isAnonymized: selectedOp?.isAnonymized,
        name: selectedOp?.name
      }
      setAnomOp(anom);

      const response = await getOperatorTooltipData({
        country: appliedFilters.countryCode,
        geohash: operatorData.geohash,
        quarter: `${appliedFilters.quarter} ${appliedFilters.year}`,
        ct: connectionType,
        dt: "Macro",
        at: aggType,
        operator,
        adminLevel1: operatorData?.adm1 || null,
        adminLevel2: operatorData?.adm2 || null,
        adminLevel3: operatorData?.adm3 || null
      }, true);
      if (response.success) {
        const processedResponseData = processResponseData(response.data, permittedOperators, response.anomData);
        const polygon = convertGeohashToPolygon(operatorData.geohash);
        setGeoTooltip({
          ...mappedOperatorData,
          polygon,
          bestOperators: response.data,
          operator,
          anomData: response?.anomData,
        });
      }
    } catch (err) {
      console.log("Erro while getting tooltip data : ", err);
    }
  };

  const onClickGeoPoly = async (operatorData) => {
    try {
      const polygon = operatorData.poly;
      let response;
      let lvls = []
      if (operatorData && operatorData.ms) {
        const adm1 = operatorData.ms.adm1;
        const adm2 = operatorData.ms.adm2 || null;
        const adm3 = operatorData.ms.adm3 || null;

        lvls = [{ [globalConfig?.adminLevelLabels?.adm1]: adm1 }]
        if (adm2) lvls.push({ [globalConfig?.adminLevelLabels?.adm2]: adm2 })
        if (adm3) lvls.push({ [globalConfig?.adminLevelLabels?.adm3]: adm3 })

        const dataTransmitMetrics = [bestOpTypeKeyMapper.data_received, bestOpTypeKeyMapper.data_transceived, bestOpTypeKeyMapper.data_transmitted];
        if (dataTransmitMetrics.includes(radioValue)) aggType = "Mean";  
        
        response = await getTopMSTooltipData({
          country: appliedFilters.countryCode,
          quarter: `${appliedFilters.quarter} ${appliedFilters.year}`,
          ct: connectionType,
          adm1: adm1,
          adm2: adm2,
          adm3: adm3,
          borderLvl: borderLvl || "ADM1",
          at: aggType,
        });
      }
      if (response && response.success) {
        setOpTooltip({
          ...operatorData,
          polygon,
          data: response.data,
          lvls: lvls
        });
      }
    } catch (err) {
      console.log("Erro while getting tooltip data : ", err);
    }
  };

  const generateMarketShareLegend = (
    radioValue,
  ) => {

    if (OP_MS_RADIO_DATA[7].value === radioValue || OP_MS_TRANSMISSION_DATA.some(item => item.value === radioValue)) {
      let minPos = Number.POSITIVE_INFINITY;
      let maxPos = Number.NEGATIVE_INFINITY;
      let maxRank = radioValue === "ms_rank" ? msRanks.length : 100;
      let typeSpecificTitle = "";
      let minFinalValue, maxFinalValue;

      const msLegend = msConfig.find(section => section.content.some(contentItem => contentItem.type === radioValue));
      
      if (msLegend ) {
        const msGradientArr = msLegend.content.find(contentItem => contentItem.type === radioValue)?.values[0]?.colorGradient.map(obj => {
          const pos = obj.pos;
          if (pos < minPos) {
            minPos = pos;
          }
          if (pos > maxPos) {
            maxPos = pos;
          }

          const { _id, ...rest } = obj;
          return rest;
        }) || [];

        typeSpecificTitle = msLegend.content.find(contentItem => contentItem.type === radioValue)?.title;

        if (radioValue === "ms") {
          minFinalValue = parseInt(minPos);
          maxFinalValue = parseInt("100");
        } else if (radioValue === "ms_rank") {
          minFinalValue = parseInt("1");
          maxFinalValue = parseInt(maxRank);
        }else if(radioValue === "data_received" || radioValue === "data_transmitted" || radioValue === "data_transceived"){
          minFinalValue = parseInt(minMaxMSOp[msType].min) || 0;
          maxFinalValue = parseInt(minMaxMSOp[msType].max) || 0;
        }

        setMarketShareLegendData([
          {
            title: typeSpecificTitle,
            gradient: { colors: msGradientArr, min: minFinalValue, max: maxFinalValue },
          },
        ]);
      }else{
          setMarketShareLegendData([
            {
              title: msType,
              gradient: { colors: defaultGradientArr, min: parseInt(minMaxMSOp[msType].min) || 0 , max: parseInt(minMaxMSOp[msType].max) || 1 },
            },
          ]);
      }
    }
    else {
      let msColorValuesArr = [];
      msConfig?.forEach((item) => {
        const section = item?.content.find(x => x.type === radioValue) || item?.content[0];
        if (section) {
          section.values.forEach((contItem) => {
            let colorObj = {};
            colorObj.text = contItem?.text;
            colorObj.markerType = contItem?.colorType;
            colorObj.markerColor = contItem?.color;
            msColorValuesArr.push(colorObj);
          });
          setMarketShareLegendData([
            {
              title: section.title,
              values: msColorValuesArr,
            },
          ]);
        }
      });

    };

  };

  const findCenter = (polygons) => {
    return {
      lat: getMiddle('lat', polygons),
      lng: getMiddle('lng', polygons)
    }
  }

  return (
    <>
      {isLoadingMarketShare && (
        <BackDropWithText text={"Loading Market Share Data..."} />
      )}
      {/* show geohashes if selected (no zoom limit) or show all geohashes when zoom reached */}
      {mapPriority === "geoTable" && msGeohashes.length > 0 && (selectedGeohashes.length || mapZoomLevel >= geohashZoom)
        ? <div className="data-on-map">
          {msGeohashes?.map((data, index) => {
            const color = getGeohashColor(data);
            return color ? (
              <Polygon
                path={convertGeohashToPolygon(data.geohash)}
                key={index}
                options={{
                  fillColor: color,
                  fillOpacity: 0.8,
                  strokeColor: "#bdbdbd",
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  clickable: true,
                  draggable: false,
                  editable: false,
                  geodesic: false,
                  zIndex: 1,
                }}
                onClick={() => {
                  onClickGeohash(data);
                }}
              />
            ) : (
              <></>
            )
          })}
        </div>
        : <div className="data-on-map">
          {msPolygons?.map((data, index) => {
            const color = getLvlColor(data);
            return color ? (
              <Polygon
                path={data.poly}
                key={index}
                options={{
                  fillColor: color,
                  fillOpacity: 0.6,
                  strokeColor: borderLvl !== "ADM1" ? "grey" : color,
                  strokeOpacity: borderLvl !== "ADM1" ? 0.8 : 0.2,
                  strokeWeight: borderLvl !== "ADM1" ? 0.5 : 0.2,
                  clickable: mapTool === MapTools.DOWNLOAD_PIN || mapTool === MapTools.DOWNLOAD_POLYGON? false: true,
                  draggable: false,
                  editable: false,
                  geodesic: false,
                  zIndex: 1,
                }}
                onClick={() => {
                  onClickGeoPoly(data);
                }}
              />
            ) : (
              <></>
            );
          })}
        </div>}
      {borderLvl === "ADM1"
        ? msPolygonBorder?.map((data, index) => {
          return <Polygon
            path={data}
            key={index}
            options={{
              fillColor: "white",
              fillOpacity: 0,
              strokeColor: "grey",
              strokeOpacity: 0.8,
              strokeWeight: 0.8,
              clickable: false,
              draggable: false,
              editable: false,
              geodesic: false,
              zIndex: 1,
            }}
          />
        })
        : <></>}
      {geoTooltip && (
        <InfoWindow
          position={{
            lat: geoTooltip.polygon[3].lat,
            lng:
              geoTooltip.polygon[3].lng +
              geohashMiddle[geoTooltip.geohash?.length || 7],
          }}
          onCloseClick={() => {
            setGeoTooltip(null);
            setAnomOp({});
          }}
          options={{ disableAutoPan: true }}
        >
          <OperatorTooltip
            supplyType={geoTooltipTypeMap[msType]}
            geohashStat={geoTooltip}
            type={TooltipType.NORMAL}
            anom={anomOp}
          />
        </InfoWindow>
      )}

      {opTooltip && (
        <InfoWindow
          position={findCenter(opTooltip?.polygon)}
          onCloseClick={() => {
            setOpTooltip(null);
          }}
          options={{ disableAutoPan: true }}
        >
          <TopMSTooltip
            supplyType={msType}
            geohashStat={opTooltip}
            type={TooltipType.NORMAL}
            operator={operator}
            topMarketShareToggle={false}
            permittedOperators={permittedOperators}
          />
        </InfoWindow>
      )}
    </>
  );
};

export default memo(OperatorMSMapElements);
